import { useCallback, useRef, useState } from 'react';
import { analyzeLogFile } from '../api/analyze';
import type { Data } from '../types';

export type UploadState = 'idle' | 'uploading' | 'success' | 'error';

export function useAnalysis() {
  const requestIdRef = useRef(0);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState('');

  const startRequest = useCallback((fileName: string) => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setSelectedFileName(fileName);
    setUploadState('uploading');
    setError(null);
    setData(null);
    return requestId;
  }, []);

  const finishSuccess = useCallback((requestId: number, result: Data) => {
    if (requestIdRef.current !== requestId) return;
    setData(result);
    setUploadState('success');
  }, []);

  const finishError = useCallback((requestId: number, message: string) => {
    if (requestIdRef.current !== requestId) return;
    setError(message);
    setUploadState('error');
  }, []);

  const analyzeFile = useCallback(async (file: File) => {
    const requestId = startRequest(file.name);

    try {
      const result = await analyzeLogFile(file);
      finishSuccess(requestId, result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      finishError(requestId, message);
    }
  }, [finishError, finishSuccess, startRequest]);

  const analyzeFromUrl = useCallback(async (url: string, fileName: string) => {
    const requestId = startRequest(fileName);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load ${fileName}`);
      }
      const blob = await response.blob();
      const file = new File([blob], fileName, {
        type: blob.type || 'text/plain',
      });
      const result = await analyzeLogFile(file);
      finishSuccess(requestId, result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Example upload failed';
      finishError(requestId, message);
    }
  }, [finishError, finishSuccess, startRequest]);

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    setUploadState('idle');
    setData(null);
    setError(null);
    setSelectedFileName('');
  }, []);

  return {
    uploadState,
    data,
    error,
    selectedFileName,
    analyzeFile,
    analyzeFromUrl,
    reset,
  };
}
